import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Clock, FileWarning, Share2, Truck, Phone } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useClienteAuthStore } from '@/stores/clienteAuthStore';
import { clienteMobileService } from '@/services/clienteMobileService';
import type { ClienteDashboard } from '@/types/clienteMobile';
import { formatCurrency } from '@/lib/formatters';
import { ClienteCityBanner } from '@/components/cliente/ClienteCityBanner';
import { ClienteVirtualLocacaoModal } from '@/components/cliente/ClienteVirtualLocacaoModal';
import { openClienteWhatsApp, CLIENTE_WHATSAPP_NUMBERS } from '@/utils/clienteWhatsApp';

function greeting(): string {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return 'Bom dia';
  if (h >= 12 && h < 18) return 'Boa tarde';
  return 'Boa noite';
}

export function ClienteDashboardPage() {
  const user = useClienteAuthStore((s) => s.user);
  const [dashboard, setDashboard] = useState<ClienteDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [virtualOpen, setVirtualOpen] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const d = await clienteMobileService.getDashboard();
      setDashboard(d);
    } catch (e: unknown) {
      setError('Não foi possível carregar o resumo. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const nome = user?.clienteFornecedor?.nome ?? 'Usuário';
  const displayName = nome
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

  const hasPending =
    dashboard &&
    (dashboard.titulos.vencidos.quantidade > 0 ||
      dashboard.titulos.aVencer.quantidade > 0 ||
      dashboard.multas.vencidas > 0 ||
      dashboard.multas.aVencer > 0);

  const showVirtualHint =
    Boolean(user?.clienteFornecedor?.marca?.trim()) ||
    Boolean(user?.clienteFornecedor?.modelo?.trim()) ||
    Boolean(user?.clienteFornecedor?.placa?.trim()) ||
    Boolean(dashboard?.cartaoLocacao?.idLocacao);

  if (loading && !dashboard) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <div className="h-10 w-10 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-muted-foreground">Carregando informações...</p>
      </div>
    );
  }

  if (error && !dashboard) {
    return (
      <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6 text-center">
        <p className="text-destructive font-medium">{error}</p>
        <Button
          className="mt-4 !bg-button-brand !text-button-brand-foreground shadow-button hover:shadow-button-hover hover:!bg-button-brand/90"
          onClick={() => load()}
        >
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div
        className="rounded-2xl p-6 text-black shadow-md relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #FFE082, #FFC107)' }}
      >
        <div className="relative z-10">
          <p className="text-sm font-medium text-black/80">{greeting()},</p>
          <h1 className="text-2xl sm:text-3xl font-bold mt-1">{displayName}</h1>
          {(user?.clienteFornecedor?.marca || user?.clienteFornecedor?.modelo) && (
            <p className="text-sm text-black/75 mt-2">
              {[user?.clienteFornecedor?.marca, user?.clienteFornecedor?.modelo].filter(Boolean).join(' · ')}
            </p>
          )}
          {hasPending && (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-black/10 px-3 py-1.5 text-sm font-semibold">
              <AlertCircle className="h-4 w-4" />
              Você tem itens pendentes
            </div>
          )}
        </div>
      </div>

      {dashboard && (
        <div>
          <h2 className="text-lg font-semibold mb-3">Resumo</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Link
              to="/cliente/financeiro?filter=vencidos"
              className="rounded-xl border border-border bg-card p-4 hover:border-amber-400/50 transition-colors"
            >
              <div className="flex items-center gap-2 text-red-600 mb-2">
                <FileWarning className="h-5 w-5" />
                <span className="text-sm font-medium">Títulos vencidos</span>
              </div>
              <p className="text-2xl font-bold">{dashboard.titulos.vencidos.quantidade}</p>
              <p className="text-xs text-muted-foreground">{formatCurrency(dashboard.titulos.vencidos.valor)}</p>
            </Link>
            <Link
              to="/cliente/financeiro?filter=a_vencer"
              className="rounded-xl border border-border bg-card p-4 hover:border-amber-400/50 transition-colors"
            >
              <div className="flex items-center gap-2 text-amber-700 mb-2">
                <Clock className="h-5 w-5" />
                <span className="text-sm font-medium">A vencer</span>
              </div>
              <p className="text-2xl font-bold">{dashboard.titulos.aVencer.quantidade}</p>
              <p className="text-xs text-muted-foreground">{formatCurrency(dashboard.titulos.aVencer.valor)}</p>
            </Link>
            <Link
              to="/cliente/financeiro?filter=vencidos&multas=1"
              className="rounded-xl border border-border bg-card p-4 hover:border-amber-400/50 transition-colors"
            >
              <div className="flex items-center gap-2 text-orange-600 mb-2">
                <AlertCircle className="h-5 w-5" />
                <span className="text-sm font-medium">Multas</span>
              </div>
              <p className="text-2xl font-bold">{dashboard.multas.vencidas + dashboard.multas.aVencer}</p>
              <p className="text-xs text-muted-foreground">{dashboard.multas.vencidas} vencidas</p>
            </Link>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-amber-300/50 bg-amber-50/80 dark:border-amber-500/30 dark:bg-amber-500/10 p-4">
        <div className="flex items-center gap-2 mb-2">
          <Share2 className="h-5 w-5 text-amber-600" />
          <h2 className="font-semibold">Indique seu amigo(a) e ganhe!</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-3">Compartilhe seu link de indicação.</p>
        <Link
          to="/cliente/indicacao"
          className="inline-flex h-10 items-center justify-center rounded-button border border-input bg-background px-4 text-sm font-medium shadow-soft-sm hover:bg-muted"
        >
          Abrir indicações
        </Link>
      </div>

      <div>
        <p className="text-sm text-muted-foreground mb-2">
          {showVirtualHint ? 'Toque no cartão virtual para ver seus dados da locação.' : ''}
        </p>
        <button
          type="button"
          className="w-full text-left rounded-2xl overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 disabled:opacity-60"
          onClick={() => showVirtualHint && setVirtualOpen(true)}
          disabled={!showVirtualHint}
        >
          <ClienteCityBanner />
        </button>
      </div>

      <div>
        <h2 className="text-lg font-semibold mb-2">Solicite seu guincho</h2>
        <p className="text-sm text-muted-foreground mb-3">Precisa de ajuda na estrada?</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Button
            type="button"
            variant="outline"
            className="h-auto py-4 flex flex-col items-start gap-1"
            onClick={() => openClienteWhatsApp(CLIENTE_WHATSAPP_NUMBERS.GUINCHO, 'Olá! Gostaria de solicitar um guincho.')}
          >
            <Truck className="h-5 w-5 text-amber-600" />
            <span className="font-semibold">Solicitar guincho</span>
            <span className="text-xs text-muted-foreground font-normal">Fale pelo WhatsApp</span>
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-auto py-4 flex flex-col items-start gap-1 border-red-200 dark:border-red-900"
            onClick={() => openClienteWhatsApp(CLIENTE_WHATSAPP_NUMBERS.SOS, 'SOS! Preciso de ajuda urgente!')}
          >
            <Phone className="h-5 w-5 text-red-600" />
            <span className="font-semibold">Central 24h</span>
            <span className="text-xs text-muted-foreground font-normal">Emergência</span>
          </Button>
        </div>
      </div>

      {dashboard &&
        dashboard.titulos.vencidos.quantidade === 0 &&
        dashboard.titulos.aVencer.quantidade === 0 &&
        dashboard.titulos.pagos.quantidade === 0 &&
        dashboard.multas.vencidas === 0 &&
        dashboard.multas.aVencer === 0 && (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-8 text-center">
            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-3" />
            <p className="font-semibold text-lg">Tudo em dia!</p>
            <p className="text-sm text-muted-foreground mt-1">Você não possui títulos ou multas pendentes.</p>
          </div>
        )}

      <ClienteVirtualLocacaoModal
        isOpen={virtualOpen}
        onClose={() => setVirtualOpen(false)}
        nomeLocatario={user?.clienteFornecedor?.nome ?? ''}
        placa={user?.clienteFornecedor?.placa}
        marca={user?.clienteFornecedor?.marca}
        modelo={user?.clienteFornecedor?.modelo}
        codigoCliente={user?.clienteFornecedor?.codigo}
        empresaNome={user?.clienteFornecedor?.company?.name}
        vencidosQuantidade={dashboard?.titulos.vencidos.quantidade ?? 0}
        vencidosValor={dashboard?.titulos.vencidos.valor ?? 0}
        cartaoLocacao={dashboard?.cartaoLocacao ?? null}
      />
    </div>
  );
}
